import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Delete,
  PersonAdd,
  AdminPanelSettings,
  Person,
  Warning,
  People,
  Storage,
  HowToReg,
  Business,
} from "@mui/icons-material";
import { useAuth } from "../context/EnterpriseAuthContext";
import { formatDate } from "../utils/dateUtils";
import { supabase } from "../lib/supabase";
import { PageContainer, PageHeader, SectionCard, EmptyState, IconTile } from "./pagekit";

const ROLE_LABELS = {
  member: "Member",
  template_creator: "Template Creator",
  account_admin: "Account Admin",
  local_admin: "Local Admin",
};

const formatRoleLabel = (role) =>
  ROLE_LABELS[role] ||
  (role || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function AdminPanel() {
  const { state: authState, actions: authActions } = useAuth();
  const [users, setUsers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editFormData, setEditFormData] = useState({
    username: "",
    email: "",
    role: "user",
    password: "",
  });
  const [inviteFormData, setInviteFormData] = useState({
    email: "",
    role: "member",
    password: "",
    username: "",
  });
  const [accountName, setAccountName] = useState("");

  useEffect(() => {
    if (authState.activeAccount) {
      setAccountName(authState.activeAccount.name);
    }
  }, [authState.activeAccount]);

  const loadData = useCallback(async () => {
    if (!authState.activeAccount) return;

    try {
      // Fetch users (members) of the current account
      const { data: members, error: membersError } = await supabase
        .from("memberships")
        .select(`
          id,
          role,
          status,
          profiles (id, username, email, created_at)
        `)
        .eq("account_id", authState.activeAccount.id);

      if (membersError) throw membersError;
      setUsers(members.map(m => ({ ...m.profiles, membership: m })));

      // Fetch pending join requests for the current account
      const { data: requests, error: requestsError } = await supabase
        .from("join_requests")
        .select(`
          id,
          status,
          profiles (id, username, email)
        `)
        .eq("account_id", authState.activeAccount.id)
        .eq("status", "pending");

      if (requestsError) throw requestsError;
      setJoinRequests(requests);

      // Fetch projects and templates for storage/activity statistics
      const { data: projectRows, error: projectsError } = await supabase
        .from("projects")
        .select("id, user_id, content, created_at, updated_at")
        .eq("account_id", authState.activeAccount.id);

      if (projectsError) throw projectsError;
      setProjects(projectRows || []);

      const { data: templateRows, error: templatesError } = await supabase
        .from("templates")
        .select("id, user_id, content, created_at, updated_at")
        .eq("account_id", authState.activeAccount.id);

      if (templatesError) throw templatesError;
      setTemplates(templateRows || []);

    } catch (error) {
      console.error("Error loading admin data:", error);
      setError("Failed to load account data. " + error.message);
    }
  }, [authState.activeAccount]);

  // Load data on component mount and when active account changes
  useEffect(() => {
    if (authActions.isAccountAdmin()) {
      loadData();
    }
  }, [authActions, loadData]);

  const handleApproveRequest = async (request) => {
    try {
      // The server checks that we are an admin of the request's workspace, adds the requester
      // as a member, marks the request approved and updates the team's paid seats.
      const { data, error: approveError } = await supabase.functions.invoke("invite-user", {
        body: { action: "approve-join-request", requestId: request.id },
      });

      if (approveError) throw approveError;
      if (data?.error) throw new Error(data.error);

      setSuccess("User has been added to the account.");
      loadData(); // Refresh the data
    } catch (error) {
      setError("Failed to approve request: " + error.message);
    }
  };

  const handleDenyRequest = async (request) => {
    try {
      const { error } = await supabase
        .from("join_requests")
        .update({ status: 'denied' })
        .eq('id', request.id);
        
      if (error) throw error;
      
      setSuccess("Request has been denied.");
      loadData(); // Refresh the data
    } catch (error) {
      setError("Failed to deny request: " + error.message);
    }
  };
  
  const handleAccountUpdate = async () => {
    setError("");
    setSuccess("");
    if (!accountName.trim()) {
      setError("Organization name cannot be empty.");
      return;
    }
    try {
      // Workspace rows are not client-writable; the RPC checks admin rights on the server.
      const { error } = await supabase.rpc("update_account_name", {
        p_account: authState.activeAccount.id,
        p_name: accountName.trim(),
      });

      if (error) throw error;

      setSuccess("Organization name updated successfully.");
      // We also need to refresh the auth context to see the new name in the navbar
      authActions.refreshAccountData();
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (error) {
      console.error("Error updating account:", error);
      setError("Failed to update organization name: " + error.message);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      username: user.username,
      email: user.email,
      role: user.membership.role,
      password: "",
    });
    setEditDialogOpen(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleInviteUser = () => {
    setInviteFormData({ email: "", role: "member", password: "", username: "" });
    setInviteDialogOpen(true);
  };

  const handleInviteSubmit = async () => {
    setError("");
    setSuccess("");
    if (!inviteFormData.email) {
      setError("Email is required.");
      return;
    }

    try {
      // If password is provided, create user directly via signup function
      if (inviteFormData.password.trim()) {
        if (!inviteFormData.username.trim()) {
          setError("Username is required when creating account with password.");
          return;
        }

        const { data, error } = await supabase.functions.invoke("signup", {
          body: {
            email: inviteFormData.email,
            password: inviteFormData.password,
            username: inviteFormData.username,
            role: inviteFormData.role,
            accountId: authState.activeAccount.id,
            redirectToBase: window.location.origin,
          },
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        setSuccess(`User account created successfully for ${inviteFormData.email}.`);
      } else {
        // If no password, send invitation as before
        const { data, error } = await supabase.functions.invoke("invite-user", {
          body: {
            email: inviteFormData.email,
            role: inviteFormData.role,
            accountId: authState.activeAccount.id,
            redirectToBase: window.location.origin,
          },
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        // The server answers the same way for new and existing emails (so admins cannot probe
        // who is registered). New users get an email; existing users get no email and see the
        // invitation in the app (PendingInvitesPrompt) the next time they sign in.
        setSuccess(
          `Invitation created for ${inviteFormData.email}. New users get an email to set up their account. If they already use Quill, they will see the invitation the next time they sign in.`
        );
      }

      setInviteDialogOpen(false);
      loadData(); // Refresh user list
      setTimeout(() => setSuccess(""), 8000);

    } catch (error) {
      console.error("Error creating/inviting user:", error);
      // When an Edge Function fails, the specific error is in the response body.
      // We need to parse it from the error context.
      let specificError = `Failed to ${inviteFormData.password.trim() ? 'create user account' : 'send invitation'}: ${error.message}`; // Fallback message
      if (error.context && typeof error.context.json === 'function') {
        try {
          const errorBody = await error.context.json();
          if (errorBody.error) {
            specificError = errorBody.error;
          }
        } catch (e) {
          // Ignore if parsing fails, just use the default message
          console.error("Could not parse error response:", e);
        }
      }
      setError(specificError);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    setError("");
    setSuccess("");
    
    try {
      // Use the edge function to update the user role
      const { data, error } = await supabase.functions.invoke("update-user-role", {
        body: {
          membershipId: selectedUser.membership.id,
          newRole: editFormData.role,
          accountId: authState.activeAccount.id,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      // Password changes are more complex and should be handled separately
      // For now, we are just updating the role.
      if (editFormData.password.trim()) {
         console.warn("Password change from admin panel not implemented in multi-tenant setup yet.");
         setSuccess("User role updated successfully. Password changes must be done by the user.");
      } else {
        setSuccess("User role updated successfully.");
      }

      setEditDialogOpen(false);
      loadData(); // Refresh the data
      setTimeout(() => setSuccess(""), 3000);

    } catch (error) {
      console.error("Error updating user role:", error);
      
      // Handle edge function specific errors
      let errorMessage = "Failed to update user role: " + error.message;
      if (error.context && typeof error.context.json === 'function') {
        try {
          const errorBody = await error.context.json();
          if (errorBody.error) {
            errorMessage = errorBody.error;
          }
        } catch (e) {
          // Ignore if parsing fails, just use the default message
          console.error("Could not parse error response:", e);
        }
      }
      setError(errorMessage);
    }
  };

  const totalUsers = users.length;
  const totalAdmins = useMemo(
    () => users.filter((u) => u.membership.role === "account_admin").length,
    [users]
  );

  const handleDeleteSubmit = async () => {
    if (!selectedUser) return;
    setError("");
    setSuccess("");

    if (totalUsers <= 1) {
      setError("Cannot remove the last user in this organization.");
      return;
    }

    const isLastAdmin =
      selectedUser.membership.role === "account_admin" && totalAdmins <= 1;

    if (isLastAdmin) {
      setError("Cannot remove the last account admin. Add another admin first.");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("remove-membership", {
        body: {
          membershipId: selectedUser.membership.id,
          accountId: authState.activeAccount.id,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSuccess(data?.message ?? "User has been removed from the account.");
      setDeleteDialogOpen(false);
      loadData();
      setTimeout(() => setSuccess(""), 3000);

    } catch (error) {
      console.error("Error removing user from account:", error);
      let errorMessage = "Failed to remove user: " + error.message;
      if (error.context && typeof error.context.json === "function") {
        try {
          const errorBody = await error.context.json();
          if (errorBody.error) {
            errorMessage = errorBody.error;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
      }
      setError(errorMessage);
    }
  };

  // Estimate storage/activity per user from the content of their projects and templates
  const { userStats, totalData } = useMemo(() => {
    const stats = users.map((user) => {
      const userProjects = projects.filter((p) => p.user_id === user.id);
      const userTemplates = templates.filter((t) => t.user_id === user.id);
      const items = [...userProjects, ...userTemplates];

      const dataSize = items.reduce(
        (sum, item) => sum + (item.content ? item.content.length : 0),
        0
      );

      const lastActivity = items.reduce((latest, item) => {
        const timestamp = item.updated_at || item.created_at;
        return timestamp && (!latest || timestamp > latest) ? timestamp : latest;
      }, null);

      return {
        id: user.id,
        username: user.username,
        role: user.membership.role,
        projects: userProjects.length,
        templates: userTemplates.length,
        dataSize,
        lastActivity,
      };
    });

    return {
      userStats: stats,
      totalData: [...projects, ...templates].reduce(
        (sum, item) => sum + (item.content ? item.content.length : 0),
        0
      ),
    };
  }, [users, projects, templates]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!authActions.isAccountAdmin()) {
    return (
      <PageContainer width="narrow">
        <Alert severity="error">
          You do not have permission to view this page.
        </Alert>
      </PageContainer>
    );
  }

  const adminCount = users.filter((u) => u.membership.role === "account_admin").length;

  return (
    <PageContainer>
      <PageHeader
        title="Admin"
        description="Manage who is in your workspace, their roles, and how the workspace is used."
        actions={
          <Button variant="contained" startIcon={<PersonAdd />} onClick={handleInviteUser}>
            Add user
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Admin sections"
        >
          <Tab label="Users" icon={<People fontSize="small" />} iconPosition="start" />
          <Tab
            label={
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                Join requests
                {joinRequests.length > 0 && <Chip size="small" color="primary" label={joinRequests.length} />}
              </Box>
            }
            icon={<HowToReg fontSize="small" />}
            iconPosition="start"
          />
          <Tab label="Workspace" icon={<Business fontSize="small" />} iconPosition="start" />
          <Tab label="Usage" icon={<Storage fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <SectionCard flush title={`Users (${users.length})`} description={`${adminCount} admin${adminCount === 1 ? "" : "s"}`}>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 640 }} aria-label="Users">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "var(--q-bg-subtle)" }}>User</TableCell>
                  <TableCell sx={{ bgcolor: "var(--q-bg-subtle)" }}>Role</TableCell>
                  <TableCell sx={{ bgcolor: "var(--q-bg-subtle)" }}>Joined</TableCell>
                  <TableCell align="right" sx={{ bgcolor: "var(--q-bg-subtle)" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const removeBlockedReason =
                    totalUsers <= 1
                      ? "You cannot remove the last user in this organization."
                      : user.membership.role === "account_admin" && totalAdmins <= 1
                      ? "You cannot remove the last account admin."
                      : "";
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>
                            {(user.username || user.email || "?").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {user.username}
                              {user.id === authState.user.id && (
                                <Typography component="span" variant="caption" color="text.secondary"> (you)</Typography>
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap component="div">{user.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.membership.role === "account_admin" ? <AdminPanelSettings /> : <Person />}
                          label={formatRoleLabel(user.membership.role)}
                          color={user.membership.role === "account_admin" ? "primary" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => handleEditUser(user)} aria-label={`Edit ${user.username}`}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.id !== authState.user.id && (
                          <Tooltip title={removeBlockedReason || "Remove from workspace"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user)}
                                aria-label={`Remove ${user.username}`}
                                disabled={Boolean(removeBlockedReason)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {activeTab === 1 && (
        <SectionCard flush title={`Pending join requests (${joinRequests.length})`}>
          {joinRequests.length === 0 ? (
            <EmptyState
              compact
              icon={<HowToReg />}
              title="No pending requests"
              description="People who sign up with your company email domain can ask to join. Their requests appear here."
            />
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {joinRequests.map((request) => (
                <Box
                  component="li"
                  key={request.id}
                  sx={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    borderBottom: 1,
                    borderColor: "var(--q-row-divider)",
                    "&:last-of-type": { borderBottom: 0 },
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                    {(request.profiles?.username || request.profiles?.email || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{request.profiles?.username}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{request.profiles?.email}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => handleDenyRequest(request)}>
                      Deny
                    </Button>
                    <Button variant="contained" size="small" onClick={() => handleApproveRequest(request)}>
                      Approve
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      )}

      {activeTab === 2 && (
        <SectionCard title="Workspace" description="Shown to everyone in your organization.">
          <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 1.5, maxWidth: 640 }}>
            <TextField
              label="Organization name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              sx={{ flex: "1 1 280px" }}
            />
            <Button variant="contained" onClick={handleAccountUpdate} sx={{ minHeight: 40 }}>
              Save changes
            </Button>
          </Box>
        </SectionCard>
      )}

      {activeTab === 3 && (
        <>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
            {[
              { label: "Users", value: users.length, icon: <People /> },
              { label: "Admins", value: adminCount, icon: <AdminPanelSettings /> },
              { label: "Storage used", value: formatBytes(totalData), icon: <Storage />, hint: "Estimated from project and template content" },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "var(--q-radius-card, 16px)",
                }}
              >
                <IconTile tone="neutral" size={36}>{stat.icon}</IconTile>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" component="div" sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.15 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {stat.label}{stat.hint ? ` · ${stat.hint}` : ""}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <SectionCard flush title="Activity by user">
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 600 }} aria-label="Activity by user">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "var(--q-bg-subtle)" }}>User</TableCell>
                    <TableCell align="right" sx={{ bgcolor: "var(--q-bg-subtle)" }}>Projects</TableCell>
                    <TableCell align="right" sx={{ bgcolor: "var(--q-bg-subtle)" }}>Templates</TableCell>
                    <TableCell align="right" sx={{ bgcolor: "var(--q-bg-subtle)" }}>Data size</TableCell>
                    <TableCell sx={{ bgcolor: "var(--q-bg-subtle)" }}>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userStats.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                        No activity recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    userStats.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {user.username}
                            {user.role === "account_admin" && (
                              <Chip size="small" label={formatRoleLabel(user.role)} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{user.projects}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{user.templates}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{formatBytes(user.dataSize)}</TableCell>
                        <TableCell>{user.lastActivity ? formatDate(user.lastActivity) : "Never"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </>
      )}

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              value={editFormData.username}
              onChange={(e) =>
                setEditFormData({ ...editFormData, username: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) =>
                setEditFormData({ ...editFormData, email: e.target.value })
              }
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={editFormData.role}
                label="Role"
                onChange={(e) =>
                  setEditFormData({ ...editFormData, role: e.target.value })
                }
              >
                <MenuItem value="member">{formatRoleLabel("member")}</MenuItem>
                <MenuItem value="template_creator">{formatRoleLabel("template_creator")}</MenuItem>
                <MenuItem value="account_admin">{formatRoleLabel("account_admin")}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="New Password (leave blank to keep current)"
              type="password"
              value={editFormData.password}
              onChange={(e) =>
                setEditFormData({ ...editFormData, password: e.target.value })
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Warning color="error" sx={{ mr: 1 }} />
          Confirm Remove User
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove user "{selectedUser?.username}" from this account?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will not delete the user's account, but will revoke their access to this account's resources.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteSubmit}
            color="error"
            variant="contained"
          >
            Remove User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the email address and role for the new user. 
              {inviteFormData.password.trim() ? 
                " Providing a password will create the account immediately." : 
                " Leave password blank to send an email invitation instead."
              }
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Email"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => {
                setInviteFormData({ ...inviteFormData, email: e.target.value });
              }}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Username"
              value={inviteFormData.username}
              onChange={(e) => {
                setInviteFormData({ ...inviteFormData, username: e.target.value });
              }}
              margin="normal"
              required={inviteFormData.password.trim() !== ""}
              helperText={inviteFormData.password.trim() ? "Required when creating account with password" : "Optional for invitations"}
            />
            <TextField
              fullWidth
              label="Password (optional)"
              type="password"
              value={inviteFormData.password}
              onChange={(e) => {
                setInviteFormData({ ...inviteFormData, password: e.target.value });
              }}
              margin="normal"
              helperText="Leave blank to send invitation email, or provide password to create account immediately"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteFormData.role}
                label="Role"
                onChange={(e) =>
                  setInviteFormData({ ...inviteFormData, role: e.target.value })
                }
              >
                <MenuItem value="member">{formatRoleLabel("member")}</MenuItem>
                <MenuItem value="template_creator">{formatRoleLabel("template_creator")}</MenuItem>
                <MenuItem value="account_admin">{formatRoleLabel("account_admin")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained">
            {inviteFormData.password.trim() ? 'Create Account' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}

export default AdminPanel;
